import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

export type ToastVariant = "info" | "success" | "warning" | "error";

interface Toast {
  id: number;
  variant: ToastVariant;
  message: string;
}

interface ToastContextValue {
  toast: (message: string, variant?: ToastVariant) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const TOAST_TTL_MS = 5_000;
const MAX_TOASTS = 3;
let nextId = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const toast = useCallback((message: string, variant: ToastVariant = "info") => {
    const id = ++nextId;
    setToasts((prev) => [...prev.slice(-(MAX_TOASTS - 1)), { id, variant, message }]);
  }, []);

  // Auto-dismiss after TTL
  useEffect(() => {
    if (toasts.length === 0) return;
    const oldest = toasts[0];
    const timer = setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== oldest.id));
    }, TOAST_TTL_MS);
    return () => clearTimeout(timer);
  }, [toasts]);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      {toasts.length > 0 && (
        <div className="mt-toast-stack" role="status" aria-live="polite">
          {toasts.map((t) => (
            <div
              key={t.id}
              className={`mt-toast mt-toast--${t.variant}`}
              onClick={() =>
                setToasts((prev) => prev.filter((x) => x.id !== t.id))
              }
            >
              {t.message}
            </div>
          ))}
        </div>
      )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Defensive: outside provider — no-op rather than crash, useful for tests.
    return { toast: () => undefined };
  }
  return ctx;
}

// Module-level shim so non-React modules (e.g. mtFetch) can fire toasts.
let externalEmit: ((msg: string, v?: ToastVariant) => void) | null = null;

export function registerExternalToastEmitter(
  fn: (msg: string, v?: ToastVariant) => void,
): void {
  externalEmit = fn;
}

export function emitExternalToast(message: string, variant: ToastVariant = "info"): void {
  externalEmit?.(message, variant);
}
